import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import {
  catchError,
  finalize,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";

import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import {
  AppLoadError,
  AppSubmitError,
  IInfo,
  InfoRelease,
} from "../shared/models";
import { InfoReleaseService } from "../shared/services/api/info-release.service";
import { SharedStoreService } from "./shared-store.service";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class InfoReleaseStoreService {
  private destroyed$ = new Subject<void>();

  // State subjects
  private _infoRelease = new BehaviorSubject<InfoRelease | null>(null);
  private _error = new BehaviorSubject<AppLoadError | null>(null);
  private _loading = new BehaviorSubject<boolean>(false);

  // State observables
  infoRelease$: Observable<InfoRelease | null> =
    this._infoRelease.asObservable();
  error$: Observable<AppLoadError | null> = this._error.asObservable();
  loading$: Observable<boolean> = this._loading.asObservable();

  get infoRelease() {
    return this._infoRelease.getValue();
  }

  constructor(
    private infoReleaseService: InfoReleaseService,
    private applicantInfoStore: ApplicantInfoStoreService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  selectInfoRelease(): Observable<InfoRelease> {
    return this.infoRelease$.pipe(map((infoRelease) => infoRelease));
  }
  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  /**
   * Load the InfoRelease state.
   * @GET
   */
  loadInfoRelease(): Observable<InfoRelease> {
    const stored = this.infoRelease;
    if (stored) {
      // Emit stored value if already present
      return;
    }

    this._loading.next(true);

    return this.infoReleaseService.loadInfoRelease().pipe(
      withLatestFrom(this.applicantInfoStore.selectApplicationID()),
      switchMap(([info, appId]) => {
        const infoRelease =
          info && Object.keys(info).length
            ? new InfoRelease(appId, info)
            : new InfoRelease(appId);

        this._infoRelease.next(infoRelease);
        return of(infoRelease);
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err.error
          ? new AppLoadError(err.error.id, err.error.message)
          : new AppLoadError();
        this._error.next(error);
        return of(null);
      }),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Submit InfoRelease state.
   * @param infoRelease The InfoRelease data to submit.
   * @POST
   */
  submitInfoRelease(infoRelease: InfoRelease) {
    this._loading.next(true);

    const submitPayload: IInfo = {
      ...infoRelease,
      relationship: "",
      title: "",
      firstName: "",
      lastName: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      phone: {
        countryCode: "",
        number: "",
      },
      cellPhone: {
        countryCode: "",
        number: "",
      },
      email: "",
    };

    return this.infoReleaseService.submitInfoRelease(submitPayload).pipe(
      switchMap(() => {
        this._infoRelease.next(infoRelease);
        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "7",
          step_name: "info-release",
        });

        this.sharedStore.triggerNavigationAfterSubmit("INFO_RELEASE");
        return of(infoRelease);
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err.error
          ? new AppSubmitError(err.error.id, err.error.message)
          : new AppSubmitError();
        this._error.next(error);
        return of(null);
      }),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Cleanup resources.
   */
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
